import React, { useRef, useEffect, useState } from "react";
import {
  CameraIcon,
  PhotoIcon,
  XMarkIcon,
  QuestionMarkCircleIcon,
  ArrowPathIcon
} from "@heroicons/react/24/outline";
import CameraTroubleshooting from "../CameraTroubleshooting";
import { FileUploadPermission } from "./constants";

interface CameraCaptureProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
  onPhotoLibrary: () => void;
  permissions: string[] | null;
}

const CameraCapture: React.FC<CameraCaptureProps> = ({
  isOpen,
  onClose,
  onCapture,
  onPhotoLibrary,
  permissions
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cameraFacingRef = useRef<"environment" | "user">("environment");

  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(
    null
  );
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);
  const [showManualStart, setShowManualStart] = useState(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowManualStart(false);
      setIsStreaming(false);
      setCapturedImage(null);

      // Wait for the component to render before starting camera
      setTimeout(() => {
        startCamera();
      }, 100);
    } else {
      stopCamera();
      // Clean up captured image URL
      if (capturedImage) {
        URL.revokeObjectURL(capturedImage);
        setCapturedImage(null);
      }
    }

    return () => {
      stopCamera();
      // Clean up captured image URL on unmount
      if (capturedImage) {
        URL.revokeObjectURL(capturedImage);
      }
    };
  }, [isOpen]);

  // Effect to handle camera setup when video element is available
  useEffect(() => {
    if (isOpen && videoElement && streamRef.current && !isStreaming) {
      videoElement.srcObject = streamRef.current;

      videoElement
        .play()
        .then(() => {
          setIsStreaming(true);
        })
        .catch((err) => {
          setShowManualStart(true);
        });
    }
  }, [videoElement, isOpen, isStreaming]);

  const startCamera = async (facingMode?: "environment" | "user") => {
    const facing = facingMode ?? cameraFacingRef.current;
    try {
      setError(null);
      setIsLoading(true);

      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setError("Camera is not supported on this device or browser.");
        return;
      }

      // Try to get camera permissions
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facing,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      });

      // Store the stream - useEffect will handle video setup
      streamRef.current = stream;

      // If video element is already available, the useEffect will handle it
      if (videoElement) {
      } else {
        // Show manual start button after 3 seconds if no video element
        setTimeout(() => {
          if (!isStreaming) {
            setShowManualStart(true);
          }
        }, 3000);
      }
    } catch (err: any) {
      console.error("❌ Error accessing camera:", err);

      // Provide specific error messages based on error type
      if (err.name === "NotAllowedError") {
        setError(
          "Camera access denied. Please allow camera permissions and try again."
        );
      } else if (err.name === "NotFoundError") {
        setError("No camera found on this device.");
      } else if (err.name === "NotSupportedError") {
        setError("Camera is not supported on this browser.");
      } else if (err.name === "NotReadableError") {
        setError("Camera is already in use by another application.");
      } else {
        setError(
          "Unable to access camera. Please check permissions and try again."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setIsStreaming(false);
    }
  };

  const retryCamera = async () => {
    setIsRetrying(true);
    setError(null);

    // Stop any existing stream first
    stopCamera();

    // Wait a moment before retrying
    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      await startCamera();
    } finally {
      setIsRetrying(false);
    }
  };

  const capturePhoto = async () => {
    const video = videoRef.current || videoElement;
    if (!video || !canvasRef.current || !isStreaming) {
      return;
    }

    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    if (!context) {
      return;
    }

    // Check if video is ready
    if (video.readyState < 2) {
      return;
    }

    try {
      setIsCapturing(true);

      // Set canvas dimensions to match video
      const videoWidth = video.videoWidth || video.clientWidth;
      const videoHeight = video.videoHeight || video.clientHeight;

      canvas.width = videoWidth;
      canvas.height = videoHeight;

      // Draw the video frame to canvas
      context.drawImage(video, 0, 0, videoWidth, videoHeight);

      // Convert canvas to blob and create File
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, "image/jpeg", 0.8);
      });

      if (blob) {
        // Create preview URL
        const imageUrl = URL.createObjectURL(blob);
        setCapturedImage(imageUrl);

        // Don't close immediately, show preview first
        // onCapture(file);
        // onClose();
      } else {
        console.error("Failed to create blob from canvas");
        // Fallback: try with different quality
        canvas.toBlob(
          (fallbackBlob) => {
            if (fallbackBlob) {
              const imageUrl = URL.createObjectURL(fallbackBlob);
              setCapturedImage(imageUrl);
            } else {
              console.error("Fallback capture also failed");
            }
          },
          "image/jpeg",
          0.5
        );
      }
    } catch (error) {
      console.error("Error capturing photo:", error);
    } finally {
      setIsCapturing(false);
    }
  };

  const handleUsePhoto = () => {
    if (capturedImage) {
      // Convert the image URL back to a file
      fetch(capturedImage)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], `camera-capture-${Date.now()}.jpg`, {
            type: "image/jpeg"
          });
          onCapture(file);
          onClose();
        });
    }
  };

  const handleRetakePhoto = async () => {
    if (capturedImage) {
      URL.revokeObjectURL(capturedImage);
      setCapturedImage(null);
    }

    setIsStreaming(false);

    stopCamera();
    await new Promise((r) => setTimeout(r, 200));
    startCamera();
  };

  const setVideoRef = (element: HTMLVideoElement | null) => {
    if (element && !videoElement) {
      setVideoElement(element);
    } else if (!element && videoElement) {
      setVideoElement(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
      <div className="relative bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Take Photo</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {error ? (
          <div className="text-center py-8">
            <div className="mb-4">
              <p className="text-red-600 mb-2 font-medium">{error}</p>
              <div className="text-sm text-gray-600 space-y-1">
                <p>
                  <strong>To fix this issue:</strong>
                </p>
                <ul className="text-left max-w-sm mx-auto space-y-1">
                  <li>
                    • Check if camera permissions are allowed in browser
                    settings
                  </li>
                  <li>• Make sure no other app is using the camera</li>
                  <li>
                    • Try refreshing the page and allowing permissions again
                  </li>
                  <li>
                    • On mobile: Check if camera is enabled in device settings
                  </li>
                </ul>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={retryCamera}
                  disabled={isRetrying}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isRetrying ? "Retrying..." : "Try Again"}
                </button>

                <button
                  type="button"
                  onClick={() => setShowTroubleshooting(true)}
                  className="px-3 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
                  title="Get detailed help"
                >
                  <QuestionMarkCircleIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="text-xs text-gray-500 text-center">
                If the issue persists, try using `&quot;Choose from
                Photos&quot;` instead
              </div>
            </div>
          </div>
        ) : isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Starting camera...</p>
          </div>
        ) : capturedImage ? (
          <>
            <div className="relative bg-gray-900 rounded-lg overflow-hidden mb-4">
              <img
                src={capturedImage}
                alt="Captured photo"
                className="w-full h-64 object-cover"
              />
            </div>

            <div className="flex justify-center space-x-4">
              <button
                type="button"
                onClick={handleRetakePhoto}
                className="flex items-center space-x-2 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                <span>Retake Photo</span>
              </button>

              <button
                type="button"
                onClick={handleUsePhoto}
                className="flex items-center space-x-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <span>Use This Photo</span>
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="relative bg-gray-900 rounded-lg overflow-hidden mb-4">
              <video
                ref={setVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-64 object-cover"
                style={{
                  backgroundColor: "#000",
                  minHeight: "256px"
                }}
              />
              <canvas ref={canvasRef} className="hidden" />
            </div>

            <div className="flex justify-center space-x-4">
              {!isStreaming && showManualStart ? (
                <button
                  type="button"
                  onClick={() => {
                    const video = videoRef.current || videoElement;
                    if (video && streamRef.current) {
                      video
                        .play()
                        .then(() => {
                          setIsStreaming(true);
                          setShowManualStart(false);
                        })
                        .catch((err) => {});
                    }
                  }}
                  className="flex items-center space-x-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  <CameraIcon className="h-5 w-5" />
                  <span>Start Camera</span>
                </button>
              ) : isStreaming ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      const newFacing =
                        cameraFacingRef.current === "environment"
                          ? "user"
                          : "environment";

                      cameraFacingRef.current = newFacing;
                      stopCamera();
                      setTimeout(() => startCamera(newFacing), 300);
                    }}
                    className="flex items-center space-x-2 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    <ArrowPathIcon className="h-5 w-5" />
                    <span>Rotate</span>
                  </button>
                  <button
                    type="button"
                    onClick={capturePhoto}
                    disabled={isCapturing}
                    className="flex items-center space-x-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CameraIcon className="h-5 w-5" />
                    <span>
                      {isCapturing ? "Capturing..." : "Capture Photo"}
                    </span>
                  </button>
                </>
              ) : (
                <div className="flex items-center space-x-2 px-6 py-3 bg-gray-400 text-white rounded-lg">
                  <CameraIcon className="h-5 w-5" />
                  <span>Starting Camera...</span>
                </div>
              )}
            </div>

            {isStreaming && (
              <div className="text-center mt-2">
                <div className="inline-flex items-center space-x-2 text-sm text-green-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Camera ready</span>
                </div>
              </div>
            )}
          </>
        )}

        {(!permissions ||
          permissions.includes(FileUploadPermission.CHOOSE_FROM_DEVICE)) && (
          <div className="mt-4 pt-4 border-t">
            <button
              type="button"
              onClick={onPhotoLibrary}
              className="flex items-center space-x-2 w-full px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              <PhotoIcon className="h-5 w-5" />
              <span>Choose from Photos</span>
            </button>
          </div>
        )}
      </div>

      {showTroubleshooting && (
        <CameraTroubleshooting
          onClose={() => setShowTroubleshooting(false)}
          onUsePhotoLibrary={() => {
            setShowTroubleshooting(false);
            onPhotoLibrary();
          }}
        />
      )}
    </div>
  );
};

export default CameraCapture;
